'use client';

import React from 'react';
import { Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { useSeafarerProfile } from '@/contexts/SeafarerProfileContext';
import { useOnlineStatus } from '@/lib/hooks/useOnlineStatus';

function formatSyncTime(value: string | null) {
  if (!value) return 'Not synced yet';
  return `Last cloud sync ${new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export default function OfflineSyncBanner() {
  const isOnline = useOnlineStatus();
  const { data, cloudSyncStatus, lastCloudSyncedAt, retryCloudSync } = useSeafarerProfile();

  if (!data) return null;
  if (isOnline && (cloudSyncStatus === 'idle' || cloudSyncStatus === 'synced')) return null;

  const isSyncing = cloudSyncStatus === 'syncing';
  const isOffline = !isOnline;
  const isPending = cloudSyncStatus === 'pending' || cloudSyncStatus === 'error';

  return (
    <div className="px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-16 pt-3">
      <div
        className="max-w-screen-2xl mx-auto rounded-xl border px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
        style={{
          backgroundColor: isOffline ? 'var(--status-expiring-soon-bg)' : 'var(--secondary)',
          borderColor: isOffline ? 'rgba(217, 119, 6, 0.25)' : 'var(--border)',
          color: isOffline ? 'var(--status-expiring-soon)' : 'var(--primary)',
        }}
      >
        <div className="flex items-start gap-2.5">
          {isOffline ? <CloudOff size={16} className="mt-0.5 shrink-0" /> : <Cloud size={16} className="mt-0.5 shrink-0" />}
          <div>
            <p className="font-700">
              {isOffline ? 'Offline mode' : isSyncing ? 'Syncing saved profile' : 'Cloud sync pending'}
            </p>
            <p className="text-xs opacity-85 mt-0.5">
              {isOffline
                ? 'Your saved certificates, profile, and CV remain available on this device. Cloud sync and alerts resume when internet returns.'
                : `${formatSyncTime(lastCloudSyncedAt)}. Changes are saved locally and will retry cloud sync.`}
            </p>
          </div>
        </div>
        {isOnline && isPending ? (
          <button
            type="button"
            onClick={() => retryCloudSync()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-current px-3 py-1.5 text-xs font-700 hover:bg-white/40 transition-colors"
          >
            <RefreshCw size={12} />
            Retry sync
          </button>
        ) : null}
      </div>
    </div>
  );
}
